import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../supabaseClient'
import { CrossRefRelation, KuCrossRef } from '../types'

/**
 * CRUD for cross-pass KU mappings on the Analysis tab.
 * Scoped to a single experiment: load fetches all cross-refs where either
 * endpoint is a KU belonging to that experiment.
 */
export function useKuCrossRefs(experimentId: string | undefined) {
  const [rows, setRows] = useState<KuCrossRef[]>([])
  const [loading, setLoading] = useState(false)

  const load = useCallback(async () => {
    if (!experimentId) {
      setRows([])
      return
    }
    setLoading(true)
    // Pull every cross-ref whose from_ku_id belongs to this experiment.
    // Since refs are directional and both endpoints live in the same
    // experiment, the from-side filter is sufficient.
    const { data, error } = await supabase
      .from('ku_cross_refs')
      .select('*, from_ku:knowledge_units!from_ku_id(experiment_id)')
      .eq('from_ku.experiment_id', experimentId)
    if (error) console.error('useKuCrossRefs load error:', error)
    setRows(((data ?? []) as (KuCrossRef & { from_ku?: unknown })[]).map((r) => ({ ...r })))
    setLoading(false)
  }, [experimentId])

  useEffect(() => {
    load()
  }, [load])

  const link = useCallback(
    async (fromKuId: string, toKuId: string, relationType: CrossRefRelation, userId: string | null) => {
      const { error } = await supabase
        .from('ku_cross_refs')
        .insert({ from_ku_id: fromKuId, to_ku_id: toKuId, relation_type: relationType, created_by: userId })
      if (error && !error.message.includes('duplicate')) {
        console.error('useKuCrossRefs link error:', error)
        return
      }
      await load()
    },
    [load],
  )

  const unlink = useCallback(
    async (fromKuId: string, toKuId: string, relationType: CrossRefRelation) => {
      const { error } = await supabase
        .from('ku_cross_refs')
        .delete()
        .eq('from_ku_id', fromKuId)
        .eq('to_ku_id', toKuId)
        .eq('relation_type', relationType)
      if (error) {
        console.error('useKuCrossRefs unlink error:', error)
        return
      }
      await load()
    },
    [load],
  )

  const isLinked = useCallback(
    (from: string, to: string, rel: CrossRefRelation): boolean => {
      return rows.some((r) => r.from_ku_id === from && r.to_ku_id === to && r.relation_type === rel)
    },
    [rows],
  )

  // Helpers for the Analysis tab — answer "which CONT KUs address this REQ KU",
  // and inversely "which REQ KUs are addressed by this CONT KU".
  const targetsOf = useCallback(
    (fromKuId: string, relationType: CrossRefRelation): string[] => {
      return rows.filter((r) => r.from_ku_id === fromKuId && r.relation_type === relationType).map((r) => r.to_ku_id)
    },
    [rows],
  )

  const sourcesOf = useCallback(
    (toKuId: string, relationType: CrossRefRelation): string[] => {
      return rows.filter((r) => r.to_ku_id === toKuId && r.relation_type === relationType).map((r) => r.from_ku_id)
    },
    [rows],
  )

  return { rows, loading, load, link, unlink, isLinked, targetsOf, sourcesOf }
}
